import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  RunState,
  WorkItemFilter,
  WorkItem,
  WorkItemPriority,
  WorkItemStatus,
} from "@zworks/console-contracts";
import { workItemStatuses } from "@zworks/console-contracts";
import * as React from "react";
import { Link, useSearchParams } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ApiError,
  createWorkItem,
  createWorkItemComment,
  updateWorkItemStatus,
  workItemDetailQuery,
  workItemsQuery,
} from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { AskButton } from "@/components/ask-zot";
import { RunStateBadge } from "@/components/bespoke";
import { formatDate } from "@/lib/format";

const filters: { value: WorkItemFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "intake", label: "Intake" },
  { value: "ready", label: "Ready" },
  { value: "in-progress", label: "In progress" },
  { value: "in-review", label: "In review" },
  { value: "done", label: "Done" },
];

const statusLabels: Record<WorkItemStatus, string> = {
  intake: "Intake",
  ready: "Ready",
  "in-progress": "In progress",
  "in-review": "In review",
  done: "Done",
};

const priorityLabels: Record<WorkItemPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

/** The latest run as a labelled badge, or plain text when the item never started one. */
function latestRunCell(entry: { latestRunState: RunState | null }) {
  if (entry.latestRunState === null) return <span className="text-muted-foreground">No runs</span>;
  return <RunStateBadge state={entry.latestRunState} />;
}

function statusBadge(status: WorkItemStatus) {
  return <Badge variant="outline">{statusLabels[status]}</Badge>;
}

function Heading({ description, actions }: { description?: string; actions?: React.ReactNode }) {
  return <PageHeader title="Work items" description={description} actions={actions} />;
}

/**
 * The work items page, as the WorkItems board draws it: the status tabs, a key-or-title search,
 * one table with the latest run, and the detail drawer. A run always starts from a frozen copy
 * of the item, so editing the draft never changes a run already going.
 */
export function WorkItemsPage() {
  const { project, loaded } = useCurrentProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = React.useState<WorkItemFilter>("all");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [view, setView] = React.useState<"board" | "table">("board");
  const [dialog, setDialog] = React.useState<"none" | "new">("none");
  const selectedKey = searchParams.get("item");
  const queryClient = useQueryClient();
  const list = useQuery({
    ...workItemsQuery(project?.id ?? "", view === "board" ? "all" : filter, search, page),
    enabled: loaded && project !== null,
  });

  const [pageContext, setPageContext] = React.useState({ projectId: project?.id, view });
  if (pageContext.projectId !== project?.id || pageContext.view !== view) {
    setPageContext({ projectId: project?.id, view });
    setPage(1);
  } else if (list.data) {
    const lastPage = Math.max(1, Math.ceil(list.data.total / list.data.pageSize));
    if (page > lastPage) setPage(lastPage);
  }

  function selectFilter(next: WorkItemFilter) {
    setFilter(next);
    setPage(1);
  }

  function selectSearch(next: string) {
    setSearch(next);
    setPage(1);
  }

  function openItem(key: string) {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set("item", key);
      return next;
    });
  }

  function closeItem() {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete("item");
      return next;
    });
  }

  // A disabled query stays pending, so the empty project is decided before the query.
  if (!loaded || (project !== null && list.isPending)) {
    return (
      <>
        <Heading />
        <LoadingState label="Loading work items" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <Heading />
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its work items appear here."
        />
      </>
    );
  }

  if (list.isError) {
    const error = list.error;
    return (
      <>
        <Heading description={project.name} />
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Work items could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void list.refetch()}
          />
        )}
      </>
    );
  }

  // The project switch can disable the query mid-flight, so data is read once it exists.
  const data = list.data;
  if (!data) {
    return (
      <>
        <Heading description={project.name} />
        <LoadingState label="Loading work items" rows={6} />
      </>
    );
  }

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const currentPage = Math.min(page, totalPages);
  const from = data.total === 0 ? 0 : (currentPage - 1) * data.pageSize + 1;
  const to = (currentPage - 1) * data.pageSize + data.items.length;

  return (
    <>
      <Heading
        actions={
          <div className="flex gap-2">
            <div role="group" aria-label="Work item view" className="flex gap-1">
              <Button
                variant={view === "board" ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={view === "board"}
                onClick={() => setView("board")}
              >
                Board
              </Button>
              <Button
                variant={view === "table" ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={view === "table"}
                onClick={() => setView("table")}
              >
                Table
              </Button>
            </div>
            <Button onClick={() => setDialog("new")}>New work item</Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        {view === "table" ? (
          <Tabs value={filter} onValueChange={(value) => selectFilter(value as WorkItemFilter)}>
            <TabsList aria-label="Filter work items by status">
              {filters.map((entry) => (
                <TabsTrigger key={entry.value} value={entry.value}>
                  {entry.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        ) : null}
        <span className="ml-auto" />
        <Input
          type="search"
          role="searchbox"
          aria-label="Search work items"
          placeholder="Search key or title"
          value={search}
          onChange={(event) => selectSearch(event.target.value)}
          className="w-64"
        />
      </div>

      {view === "board" && data.items.length > 0 ? (
        <WorkItemBoard
          items={data.items}
          canMove={project.role === "owner" || project.role === "developer"}
          onOpenItem={openItem}
          onStatusChange={async (key, status) => {
            await updateWorkItemStatus(project.id, key, status);
            await queryClient.invalidateQueries({
              queryKey: ["projects", project.id, "work-items"],
            });
          }}
        />
      ) : view === "board" ? (
        <EmptyState
          title="No work items yet"
          description="Create the first work item for this project."
        />
      ) : data.items.length === 0 ? (
        <EmptyState
          title={filter === "all" && !search ? "No work items yet" : "No work items match"}
          description={
            filter === "all" && !search
              ? "Create the first work item for this project. A run starts from its frozen copy."
              : "Nothing matches this filter. Try another status or clear the search."
          }
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Work items, newest first</caption>
              <thead>
                <tr>
                  {["Key", "Title", "Source", "Status", "Priority", "Latest run", "Updated"].map(
                    (header) => (
                      <th
                        key={header}
                        scope="col"
                        className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                      >
                        {header}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {data.items.map((entry) => (
                  <tr key={entry.id} className="group hover:bg-muted/50">
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem] font-medium">
                      {entry.key}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <button
                        type="button"
                        onClick={() => openItem(entry.key)}
                        className="text-accent-text underline-offset-4 hover:underline"
                      >
                        {entry.title}
                      </button>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <Badge variant="secondary" className="font-mono">
                        {entry.source}
                      </Badge>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {statusBadge(entry.status)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {priorityLabels[entry.priority]}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {latestRunCell(entry)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                      {formatDate(entry.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
            <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
              {from}–{to} of {data.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      {view === "board" && totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {from}–{to} of {data.total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {selectedKey ? (
        <WorkItemDrawer
          key={`${project.id}:${selectedKey}`}
          projectId={project.id}
          itemKey={selectedKey}
          canComment={project.role === "owner" || project.role === "developer"}
          onClose={closeItem}
        />
      ) : null}

      <Dialog open={dialog === "new"} onOpenChange={(open) => setDialog(open ? "new" : "none")}>
        <DialogContent aria-label="New work item">
          <DialogTitle>New work item</DialogTitle>
          <NewWorkItemForm projectId={project.id} onDone={() => setDialog("none")} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function WorkItemBoard({
  items,
  canMove,
  onOpenItem,
  onStatusChange,
}: {
  items: WorkItem[];
  canMove: boolean;
  onOpenItem: (key: string) => void;
  onStatusChange: (key: string, status: WorkItemStatus) => Promise<void>;
}) {
  const [moving, setMoving] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);
  return (
    <div
      className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5"
      aria-label="Work item board"
    >
      {workItemStatuses.map((status) => (
        <section
          key={status}
          aria-label={`${statusLabels[status]} column`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const key = event.dataTransfer.getData("text/plain");
            const item = items.find((entry) => entry.key === key);
            if (!canMove || moving || !item || item.status === status) return;
            setFailed(null);
            setMoving(key);
            void onStatusChange(key, status)
              .catch(() => setFailed(`Could not move ${key}.`))
              .finally(() => setMoving(null));
          }}
          className="min-h-40 rounded-lg border bg-muted/20 p-3"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-medium">{statusLabels[status]}</h2>
            <Badge variant="outline">
              {items.filter((entry) => entry.status === status).length}
            </Badge>
          </div>
          <div className="flex flex-col gap-2">
            {items
              .filter((entry) => entry.status === status)
              .map((entry) => (
                <Card
                  key={entry.id}
                  draggable={canMove && moving === null}
                  onDragStart={(event) => event.dataTransfer.setData("text/plain", entry.key)}
                  className={`cursor-grab p-3 active:cursor-grabbing ${moving === entry.key ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{entry.key}</span>
                    <Badge variant="outline">{priorityLabels[entry.priority]}</Badge>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenItem(entry.key)}
                    className="mt-2 text-left text-sm font-medium leading-snug hover:underline"
                  >
                    {entry.title}
                  </button>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {entry.latestRunState ? (
                      <RunStateBadge state={entry.latestRunState} />
                    ) : (
                      "No runs"
                    )}
                    <span>{entry.runCount} runs</span>
                    <span>{entry.pullRequestCount} PRs</span>
                    <span>{entry.commentCount} comments</span>
                  </div>
                </Card>
              ))}
          </div>
        </section>
      ))}
      {failed ? (
        <p role="alert" className="text-sm text-danger">
          {failed}
        </p>
      ) : null}
    </div>
  );
}

/** The detail drawer: description, runs, pull requests, comments, and the Start run action. */
function WorkItemDrawer({
  projectId,
  itemKey,
  canComment,
  onClose,
}: {
  projectId: string;
  itemKey: string;
  canComment: boolean;
  onClose: () => void;
}) {
  const detail = useQuery({ ...workItemDetailQuery(projectId, itemKey), enabled: true });
  const queryClient = useQueryClient();
  const move = useMutation({
    mutationFn: (status: WorkItemStatus) => updateWorkItemStatus(projectId, itemKey, status),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "work-items"] });
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={`Work item ${itemKey}`}
      className="fixed top-0 right-0 bottom-0 z-30 flex w-[min(26rem,100%)] flex-col border-l bg-popover text-popover-foreground shadow-lg"
    >
      <div className="flex items-start justify-between gap-3 border-b p-5">
        <div>
          <p className="font-mono text-xs text-muted-foreground">{itemKey}</p>
          <h2 className="text-lg font-semibold tracking-tight">
            {detail.data?.title ?? "Work item"}
          </h2>
          {detail.data ? (
            <div className="mt-1">
              <AskButton kind="work-item" id={detail.data.id} label={detail.data.key} />
            </div>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5">
        {detail.isPending ? (
          <LoadingState label="Loading work item" rows={4} />
        ) : detail.isError ? (
          <ErrorState
            title="Work item could not be loaded"
            description={
              detail.error instanceof ApiError
                ? (detail.error.problem?.detail ?? detail.error.message)
                : "The request could not be completed."
            }
            requestId={
              detail.error instanceof ApiError ? (detail.error.requestId ?? "unknown") : "unknown"
            }
            onRetry={() => void detail.refetch()}
          />
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="font-mono">
                {detail.data.source}
              </Badge>
              {statusBadge(detail.data.status)}
              <Badge variant="outline">{priorityLabels[detail.data.priority]}</Badge>
            </div>
            {canComment ? (
              <div className="flex flex-col gap-2">
                <label htmlFor="work-item-status" className="text-sm font-medium">
                  Status
                </label>
                <select
                  id="work-item-status"
                  value={detail.data.status}
                  disabled={move.isPending}
                  onChange={(event) => move.mutate(event.target.value as WorkItemStatus)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-2 focus-visible:outline-ring disabled:bg-muted disabled:text-muted-foreground"
                >
                  {workItemStatuses.map((status) => (
                    <option key={status} value={status}>
                      {statusLabels[status]}
                    </option>
                  ))}
                </select>
                {move.isPending ? (
                  <p role="status" className="text-sm text-muted-foreground">
                    Moving work item…
                  </p>
                ) : null}
                {move.isError ? (
                  <p role="alert" className="text-sm text-danger">
                    {move.error instanceof ApiError
                      ? (move.error.problem?.detail ?? move.error.message)
                      : "The work item could not be moved."}
                  </p>
                ) : null}
              </div>
            ) : null}
            <section aria-label="Description">
              <h3 className="mb-2 text-sm font-medium">Description</h3>
              {detail.data.description ? (
                <p className="text-[0.8125rem] leading-relaxed text-muted-foreground">
                  {detail.data.description}
                </p>
              ) : (
                <p className="text-[0.8125rem] text-muted-foreground">No description yet.</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Quoted as plain text. It never changes what a run is allowed to do.
              </p>
            </section>
            <section aria-label="Runs">
              <h3 className="mb-2 text-sm font-medium">Runs</h3>
              {detail.data.runs.length === 0 ? (
                <p className="text-[0.8125rem] text-muted-foreground">
                  No runs yet. Start the first run from this item's frozen copy.
                </p>
              ) : (
                <>
                  {detail.data.runCount > detail.data.runs.length ? (
                    <p className="mb-2 text-xs text-muted-foreground">
                      Showing the latest {detail.data.runs.length} of {detail.data.runCount} runs.
                    </p>
                  ) : null}
                  <ul aria-label={`Runs for ${itemKey}`} className="flex flex-col gap-1">
                    {detail.data.runs.map((run) => (
                      <li
                        key={run.id}
                        className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                      >
                        <Link
                          to={`/runs/${encodeURIComponent(run.id)}`}
                          className="font-mono text-[0.8125rem] text-accent-text underline-offset-4 hover:underline"
                        >
                          {run.id.slice(0, 8)}
                        </Link>
                        <RunStateBadge state={run.state} />
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
            <section aria-label="Pull requests">
              <h3 className="mb-2 text-sm font-medium">Pull requests</h3>
              {detail.data.pullRequests.length === 0 ? (
                <p className="text-[0.8125rem] text-muted-foreground">No pull requests yet.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {detail.data.pullRequests.map((pullRequest) => (
                    <li key={pullRequest.id} className="rounded-md border p-3">
                      {pullRequest.url ? (
                        <a
                          className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
                          href={pullRequest.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {pullRequest.title}
                        </a>
                      ) : (
                        <p className="text-sm font-medium">{pullRequest.title}</p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {pullRequest.head} → {pullRequest.base}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge variant="outline">Checks: {pullRequest.checks}</Badge>
                        <Badge variant="outline">Review: {pullRequest.reviewState}</Badge>
                        <Badge variant="outline">Merge: {pullRequest.mergeState}</Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <WorkItemComments
              projectId={projectId}
              itemKey={itemKey}
              comments={detail.data.comments}
              commentCount={detail.data.commentCount}
              canComment={canComment}
            />
          </>
        )}
      </div>
      {canComment && detail.isSuccess ? (
        <div className="flex gap-2 border-t p-5">
          <Button asChild className="flex-1">
            <Link
              to={`/runs/new?projectId=${encodeURIComponent(projectId)}&workItem=${encodeURIComponent(itemKey)}`}
            >
              Start run
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function WorkItemComments({
  projectId,
  itemKey,
  comments,
  commentCount,
  canComment,
}: {
  projectId: string;
  itemKey: string;
  comments: {
    id: string;
    authorName: string;
    body: string;
    createdAt: string;
    origin?: string;
    citations?: { kind: string; ref: string; label: string }[];
  }[];
  commentCount: number;
  canComment: boolean;
}) {
  const queryClient = useQueryClient();
  const [body, setBody] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => createWorkItemComment(projectId, itemKey, { body }),
    onSuccess: async () => {
      setBody("");
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "work-items", itemKey],
      });
      await queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "work-items"],
      });
    },
    onError: (failure) => {
      setError(
        failure instanceof ApiError
          ? (failure.problem?.detail ?? failure.message)
          : "The request could not be completed.",
      );
    },
  });
  return (
    <section aria-label="Comments" className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">Comments</h3>
      {commentCount > comments.length ? (
        <p className="text-xs text-muted-foreground">
          Showing the latest {comments.length} of {commentCount} comments.
        </p>
      ) : null}
      {comments.length === 0 ? (
        <p className="text-[0.8125rem] text-muted-foreground">No comments yet.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {comments.map((comment) => (
            <li key={comment.id} className="rounded-md border p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  {comment.authorName}
                  {comment.origin === "agent" ? <Badge variant="secondary">Zot reply</Badge> : null}
                </span>
                <time className="text-xs text-muted-foreground" dateTime={comment.createdAt}>
                  {formatDate(comment.createdAt)}
                </time>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-[0.8125rem] leading-relaxed">
                {comment.body}
              </p>
              {comment.origin === "agent" && comment.citations && comment.citations.length > 0 ? (
                <ul aria-label="Cited records" className="mt-2 flex flex-wrap gap-1.5">
                  {comment.citations.map((citation) => (
                    <li key={citation.ref}>
                      <Badge variant="outline">{citation.label}</Badge>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {canComment ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          <label htmlFor="work-item-comment" className="text-sm font-medium">
            Add a comment
          </label>
          <p className="text-xs text-muted-foreground">
            Mention @zot to ask the agent about this item.
          </p>
          <textarea
            id="work-item-comment"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            disabled={mutation.isPending}
          />
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={!body.trim() || mutation.isPending}>
              Comment
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

/** The new-item form. A taken key is reported inline; the page never navigates away. */
function NewWorkItemForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [key, setKey] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [source, setSource] = React.useState("Jira");
  const [error, setError] = React.useState<string | null>(null);
  const created = useMutation({
    mutationFn: (body: { key: string; title: string; source: string }) =>
      createWorkItem(projectId, { ...body, status: "intake", priority: "medium" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "work-items"] });
      onDone();
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.status === 409) {
        setError(failure.problem?.detail ?? "A work item already uses this key in this project.");
      } else if (failure instanceof ApiError) {
        setError(failure.problem?.detail ?? failure.message);
      } else {
        setError("The request could not be completed.");
      }
    },
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    created.mutate({ key: key.trim(), title: title.trim(), source: source.trim() || "Manual" });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="work-item-key" className="text-sm font-medium">
          Key
        </label>
        <Input
          id="work-item-key"
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder="ORI-220"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">Capitals, digits, one hyphen: ABC-123.</p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="work-item-title" className="text-sm font-medium">
          Title
        </label>
        <Input
          id="work-item-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="What the team wants done"
          autoComplete="off"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="work-item-source" className="text-sm font-medium">
          Source
        </label>
        <Input
          id="work-item-source"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          placeholder="Jira"
          autoComplete="off"
        />
      </div>
      {error ? (
        <p role="alert" className="text-[0.8125rem] text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={created.isPending}>
          Create work item
        </Button>
      </div>
    </form>
  );
}
